import React, { useCallback } from 'react';
import { View, Pressable, StyleSheet, Platform, Keyboard } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { Tooltip } from '@/components/Tooltip';
import ChatInputSendButton from '@/components/ChatInput/ChatInputSendButton';
import DictateModeButton from '@/components/ChatInput/DictateModeButton';

/**
 * Work follow-up actions while a run is live:
 *   Dictate — while the box is empty, so follow-ups can be spoken
 *   Queue — Enter; waits until this task finishes
 *   Send now — interrupt / steer immediately
 *   Stop — always available, even while the box has text
 */
export default function WorkComposerActions({
  hasContent = false,
  onQueue,
  onSendNow,
  onStop,
  isDisabled = false,
  isLoading = false,
  onTranscript,
  onDictateStateChange,
  dictateAnchorRef,
}) {
  const { colors } = useTheme();

  const press = useCallback((fn) => {
    if (Platform.OS !== 'web') Keyboard.dismiss();
    fn?.();
  }, []);

  // Unlike a normal chat, the run being live is not a reason to block the
  // mic: users steer and queue follow-ups by voice while Work is busy.
  const showDictate = !hasContent && Platform.OS !== 'macos' && !!onTranscript;

  return (
    <View style={styles.row}>
      {showDictate ? (
        <View ref={(node) => { if (dictateAnchorRef) dictateAnchorRef.current = node; }}>
          <DictateModeButton
            onTranscript={onTranscript}
            onDictateStateChange={onDictateStateChange}
          />
        </View>
      ) : null}
      {hasContent ? (
        <>
          <Tooltip label="Queue for after this task finishes (Enter)">
            <Pressable
              onPress={() => press(onQueue)}
              disabled={isDisabled}
              accessibilityRole="button"
              accessibilityLabel="Queue"
              accessibilityHint="Waits until this task finishes, then sends"
              style={({ hovered, pressed }) => [
                styles.queue,
                {
                  borderColor: colors.border_color_primary,
                  backgroundColor: hovered || pressed
                    ? colors.solid_color_secondary
                    : 'transparent',
                },
                isDisabled && { opacity: 0.45 },
              ]}
            >
              <Text size="xxs" weight="semibold">Queue</Text>
            </Pressable>
          </Tooltip>
          <Tooltip label="Interrupt this task and send immediately">
            <Pressable
              onPress={() => press(onSendNow)}
              disabled={isDisabled}
              accessibilityRole="button"
              accessibilityLabel="Send now"
              accessibilityHint="Interrupts this task and sends now"
              style={({ hovered, pressed }) => [
                styles.sendNow,
                {
                  backgroundColor: colors.solid_color_toast,
                  opacity: isDisabled ? 0.45 : (hovered || pressed ? 0.88 : 1),
                },
              ]}
            >
              <Text size="xxs" weight="semibold" style={{ color: colors.text_color_alt }}>Send now</Text>
            </Pressable>
          </Tooltip>
        </>
      ) : null}
      <Tooltip label="Stop this task">
        <ChatInputSendButton
          onPress={() => press(onStop)}
          isDisabled={false}
          isLoading={isLoading && !hasContent}
          isInferencing
          isCancelable
        />
      </Tooltip>
    </View>
  );
}

WorkComposerActions.propTypes = {
  hasContent: PropTypes.bool,
  onQueue: PropTypes.func,
  onSendNow: PropTypes.func,
  onStop: PropTypes.func,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onTranscript: PropTypes.func,
  onDictateStateChange: PropTypes.func,
  dictateAnchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  queue: {
    height: 32,
    paddingHorizontal: 10,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none' } : {}),
  },
  sendNow: {
    height: 32,
    paddingHorizontal: 10,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none' } : {}),
  },
});
